import React, { useEffect, useState } from "react"
import { Card, Divider, message } from "antd"
import { EcommerceIntegration, EcommerceShippingRule } from "@/types/ecommerce"
import { useTranslation } from "react-i18next"
import { useDispatchRule } from "@/hooks/useDispatchRule"
import { Loader } from "@/UI"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons"
import { getEcommerceShippingRules, putEcommerceShippingRules } from "@/api/ecommerce"
import { capitalize, differenceWith, isEmpty, isEqual, orderBy } from "lodash"

import KosmoButton from "@/UI/Buttons/KosmoButton"
import DeliveryMethodSelectRule from "./DeliveryMethodSelectRule"
import { userAtom } from "@/atoms/userAtom"
import { useRecoilValue } from "recoil"

interface EcommerceShopDeliveryMethodsProps {
  ecommerceIntegration: EcommerceIntegration
}

const EcommerceShopDeliveryMethods: React.FC<EcommerceShopDeliveryMethodsProps> = ({
  ecommerceIntegration,
}) => {
  const { t } = useTranslation()

  const [shippingRules, setShippingRules] = useState<EcommerceShippingRule[]>([])
  const [toBeUpdatedRules, setToBeUpdatedRules] = useState<EcommerceShippingRule[]>([])
  const [isShippingLoading, setIsShoppingLoading] = useState<boolean>()
  const [isUpdating, setIsUpdating] = useState<boolean>()
  const { org } = useRecoilValue(userAtom)
  const { fetchDispatchRules, dispatchRules, isLoading } = useDispatchRule()

  const rulesOptions = dispatchRules.map((rule) => ({
    value: rule.id,
    label: rule.name,
  }))

  const isShippingOrDispatchLoading = isLoading || isShippingLoading
  const hasStartedEditing = !isEmpty(differenceWith(toBeUpdatedRules, shippingRules, isEqual))

  const fetchShippingRules = async () => {
    setIsShoppingLoading(true)
    try {
      const { data } = await getEcommerceShippingRules(ecommerceIntegration.id)
      setShippingRules(data)
      setToBeUpdatedRules(data)
    } catch (error) {
      console.log(error)
    } finally {
      setIsShoppingLoading(false)
    }
  }

  useEffect(() => {
    fetchShippingRules()
    fetchDispatchRules()
  }, [org?.id])

  // Update state with PUT responses
  const batchUpdateShippingRules = (responses: any) => {
    let newRules = [...shippingRules]
    responses.forEach(({ data }: { data: any }) => {
      const updatedRule = newRules?.find((rule) => rule.id === data.id)
      if (!updatedRule) {
        return
      }

      newRules = [
        ...newRules?.filter((rule) => rule.id !== data.id),
        { ...updatedRule, dispatchRuleId: data.dispatchRuleId },
      ]
    })
    setShippingRules(newRules)
  }

  // Update request on edited rules
  const handleSave = async () => {
    setIsUpdating(true)
    try {
      const modifiedRules = differenceWith(toBeUpdatedRules, shippingRules, isEqual)
      const responses = await Promise.all(
        modifiedRules.map(async (rule) =>
          putEcommerceShippingRules(ecommerceIntegration.id, rule.id, rule.dispatchRuleId)
        )
      )

      batchUpdateShippingRules(responses)

      message.success(
        t("profile.EcommerceShopDeliveryMethods.deliveryMethodsUpdateSuccess", {
          count: responses.length,
        })
      )
    } catch (error) {
      message.error(t("profile.EcommerceShopDeliveryMethods.deliveryMethodsUpdateFailed"))
    } finally {
      setIsUpdating(false)
    }
  }

  // Update local rule being edited
  const handleSelectRule = (shippingRuleId: string, dispatchRuleId: string) => {
    const updatedRule = toBeUpdatedRules?.find((rule) => rule.id === shippingRuleId)
    if (!updatedRule) {
      return
    }

    const editedRules = [
      ...toBeUpdatedRules?.filter((rule) => rule.id !== shippingRuleId),
      { ...updatedRule, dispatchRuleId },
    ]
    setToBeUpdatedRules(editedRules)
  }

  const EmptyDeliveryMethods = () => (
    <div className="flex items-center gap-x-2">
      <FontAwesomeIcon icon={faInfoCircle} />
      <p className="!mb-0">
        {t("profile.EcommerceShopDeliveryMethods.emptyShippingRules", {
          platform: capitalize(ecommerceIntegration.platform),
        })}
      </p>
    </div>
  )

  const DeliveryMethodsListHeader = () => (
    <div className="row-span-1">
      <div className="grid grid-cols-12 gap-x-4">
        <div className="col-span-6 md:col-span-4">
          <p className="font-semibold">
            {t("ecommerce.EcommerceStoreSettings.platformDeliveryMethods", {
              platform: capitalize(ecommerceIntegration.platform),
            })}
          </p>
        </div>
        <div className="col-span-6">
          <p className="font-semibold">{t("common.rule")}</p>
        </div>
      </div>
    </div>
  )

  let component = <></>

  switch (true) {
    case isShippingOrDispatchLoading:
      component = <Loader fontSize={48} />
      break
    case isEmpty(shippingRules):
      component = <EmptyDeliveryMethods />
      break
    case !isEmpty(shippingRules) && !isShippingOrDispatchLoading:
      component = (
        <div className="grid gap-4">
          <DeliveryMethodsListHeader />
          {orderBy(shippingRules, ["name"], ["desc"])?.map((shippingRule, index) => {
            const currentRule = dispatchRules.find(
              (rule) => rule.id === shippingRule.dispatchRuleId
            )
            const isBeingEdited =
              toBeUpdatedRules.find((rule) => rule.id === shippingRule.id)?.dispatchRuleId !==
              shippingRule.dispatchRuleId
            return (
              <DeliveryMethodSelectRule
                key={index}
                disabled={isUpdating}
                isBeingEdited={isBeingEdited}
                currentRule={currentRule}
                shippingRule={shippingRule}
                rulesOptions={rulesOptions}
                handleSelectRule={handleSelectRule}
              />
            )
          })}
        </div>
      )
      break
    default:
      component = <Loader fontSize={48} />
      break
  }

  return (
    <>
      <Card>
        <div className="flex flex-col gap-y-3">
          <div className="flex flex-col gap-y-1">
            <p className="font-semibold">{t("ecommerce.EcommerceStoreSettings.deliveryMethods")}</p>
            <p className="text-gray-500">
              {t("ecommerce.EcommerceStoreSettings.deliveryMethodsDescription")}
            </p>
          </div>
          <Divider style={{ margin: "8px 0" }} />
          {component}
        </div>
      </Card>
      <div className="flex">
        <KosmoButton
          id="save-shipping-rules"
          type="primary"
          onClick={handleSave}
          isDisabled={isUpdating || !hasStartedEditing}
        >
          {t("common.save")}
        </KosmoButton>
      </div>
    </>
  )
}

export default EcommerceShopDeliveryMethods
